import { AbstractAttrDescriptor, BaseEntityAttrDescriptor, BaseEntityDescriptor, FlexboxDescriptor, FlexDirection, FormControlType, toSelectables } from '@processpuzzle/base-entity';
import { DOCUMENT_INPUT_PORT_I18N_SCOPE, DOCUMENT_OUTPUT_PORT_I18N_SCOPE } from '../base-document.i18n';
import { DOCUMENT_ENTITY_NAME, DOCUMENT_INPUT_PORT_ENTITY_NAME, DOCUMENT_OUTPUT_PORT_ENTITY_NAME } from './document-entity-names';

/**
 * Attribute that identifies a port row in a URL segment, and that the embedded store keys its rows by.
 * A port has no `id` in the contract — `name` is what names it to the host, and what a binding refers to —
 * so both ends of that have to be told, here and on the owning `EMBEDDED_COMPONENTS` attribute.
 */
export const DOCUMENT_PORT_ID_FIELD = 'name';

const PORT_TYPES = ['STRING', 'NUMBER', 'BOOLEAN', 'DATE', 'OBJECT', 'ARRAY', 'ENTITY_REF', 'ENTITY_COLLECTION'];
const ENTITY_PORT_TYPES = ['ENTITY_REF', 'ENTITY_COLLECTION'];

// Shared between input and output ports — everything except `required`, `defaultValue` and
// `defaultRsqlFilter`, which only make sense for a port the host has to supply a value for.
function createCommonPortAttrs(): AbstractAttrDescriptor[] {
  const nameAttr = new BaseEntityAttrDescriptor('name', FormControlType.TEXT_BOX, 'Name', undefined, true);
  nameAttr.required = true;
  nameAttr.isHeading = true;

  const typeAttr = new BaseEntityAttrDescriptor('type', FormControlType.DROPDOWN, 'Type', toSelectables(PORT_TYPES));
  typeAttr.required = true;

  const descriptionAttr = new BaseEntityAttrDescriptor('description', FormControlType.TEXTAREA, 'Description');
  descriptionAttr.hideInTable = true;

  // entityType/attributeVisibility only mean something for ENTITY_REF / ENTITY_COLLECTION —
  // shown regardless (the form has no per-value conditional-visibility mechanism today) but
  // documented as such via placeholder text, same as base-app does for widget-only fields.
  const entityTypeAttr = new BaseEntityAttrDescriptor('entityType', FormControlType.TEXT_BOX, 'Entity type');
  entityTypeAttr.placeholder = `Only used when type is one of: ${ENTITY_PORT_TYPES.join(', ')}`;
  entityTypeAttr.hideInTable = true;

  // AttributeVisibility (mode + attributes) has no dedicated FormControlType of its own yet —
  // ADDITIONAL_PROPERTIES is an honest stand-in: it's a small, rarely-touched nested object, and
  // introducing a purpose-built control for one two-field shape isn't worth it yet. Revisit if a
  // second port-like concept needs the same shape.
  const attributeVisibilityAttr = new BaseEntityAttrDescriptor('attributeVisibility', FormControlType.ADDITIONAL_PROPERTIES, 'Attribute visibility');
  attributeVisibilityAttr.hideInTable = true;

  return [nameAttr, typeAttr, descriptionAttr, entityTypeAttr, attributeVisibilityAttr];
}

function createDocumentInputPortAttrDescriptors(): AbstractAttrDescriptor[] {
  const [nameAttr, typeAttr, descriptionAttr, entityTypeAttr, attributeVisibilityAttr] = createCommonPortAttrs();

  const requiredAttr = new BaseEntityAttrDescriptor('required', FormControlType.CHECKBOX, 'Required');

  const defaultValueAttr = new BaseEntityAttrDescriptor('defaultValue', FormControlType.TEXT_BOX, 'Default value');
  defaultValueAttr.placeholder = 'Used when required is false and the host supplies nothing';
  defaultValueAttr.hideInTable = true;

  const defaultRsqlFilterAttr = new BaseEntityAttrDescriptor('defaultRsqlFilter', FormControlType.TEXT_BOX, 'Default RSQL filter');
  defaultRsqlFilterAttr.placeholder = `Only used when type is ENTITY_COLLECTION`;
  defaultRsqlFilterAttr.hideInTable = true;

  const identityRow = new FlexboxDescriptor([nameAttr, typeAttr, requiredAttr], FlexDirection.ROW);
  identityRow.style = { 'column-gap': '10px' };

  return [new FlexboxDescriptor(
    [identityRow, descriptionAttr, entityTypeAttr, attributeVisibilityAttr, defaultValueAttr, defaultRsqlFilterAttr],
    FlexDirection.COLUMN,
  )];
}

function createDocumentOutputPortAttrDescriptors(): AbstractAttrDescriptor[] {
  const [nameAttr, typeAttr, descriptionAttr, entityTypeAttr, attributeVisibilityAttr] = createCommonPortAttrs();

  const identityRow = new FlexboxDescriptor([nameAttr, typeAttr], FlexDirection.ROW);
  identityRow.style = { 'column-gap': '10px' };

  return [new FlexboxDescriptor([identityRow, descriptionAttr, entityTypeAttr, attributeVisibilityAttr], FlexDirection.COLUMN)];
}

export function createDocumentInputPortDescriptor(): BaseEntityDescriptor {
  return new BaseEntityDescriptor({
    entityName: DOCUMENT_INPUT_PORT_ENTITY_NAME,
    attrDescriptors: createDocumentInputPortAttrDescriptors(),
    i18nScope: DOCUMENT_INPUT_PORT_I18N_SCOPE,
    componentParent: DOCUMENT_ENTITY_NAME,
    isEmbedded: true,
  });
}

export function createDocumentOutputPortDescriptor(): BaseEntityDescriptor {
  return new BaseEntityDescriptor({
    entityName: DOCUMENT_OUTPUT_PORT_ENTITY_NAME,
    attrDescriptors: createDocumentOutputPortAttrDescriptors(),
    i18nScope: DOCUMENT_OUTPUT_PORT_I18N_SCOPE,
    componentParent: DOCUMENT_ENTITY_NAME,
    isEmbedded: true,
  });
}
